import { memo, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import { AuraLayout, Button, Layout } from "lola-framework-ui-test";
import { FloatingField } from "../../components/FloatingField";
import { FUND_REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import {
  CARD_BRANDS,
  DEFAULT_ALLOWED_BRANDS,
  detectCardBrand,
  type CardBrandKey,
} from "../../constants/card-brands";
import { useStore } from "../../store/globalState";
import { useVirtualKeyboardOpen } from "../../hooks/useVirtualKeyboardOpen";
import { passesLuhn } from "../../utils/luhn";
import {
  CARDHOLDER_NAME_INPUT_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../../utils/cardholderName";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";

export interface CardStepFields {
  cardNumber: string;
  expiry: string;
  cardHolderName: string;
  cvv: string;
}

interface CardFormErrors {
  cardNumber?: string;
  expiry?: string;
  cardHolderName?: string;
  cvv?: string;
}

const BRAND_DISPLAY_ORDER: CardBrandKey[] = ["visa", "mastercard"];

const formatCardNumber = (value: string): string =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

/**
 * Gallery-safe fund-request Add Card (PAN collect).
 *
 * Matches the live widget Figma layout: title in the back navbar,
 * labeled fields, brand logos + PCI copy, Save card CTA. Luhn filters typos.
 * Continue navigates to billing address — no VGS, no register API.
 */
export const AddCardPage = memo(() => {
  const { theme } = useStore();
  const keyboardOpen = useVirtualKeyboardOpen();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation("fund-request");

  const allowedBrands = DEFAULT_ALLOWED_BRANDS;
  const showFieldErrors = pathname.includes("form-errors");
  const showBrandError = pathname.includes("brand-not-accepted");

  const [form, setForm] = useState<CardStepFields>({
    cardNumber: showFieldErrors
      ? "1234"
      : showBrandError
        ? "6011 1111 1111 1117"
        : "4242 4242 4242 4242",
    expiry: showFieldErrors ? "01/20" : "12/27",
    cardHolderName: showFieldErrors
      ? formatCardholderNameInput("Jane 123")
      : formatCardholderNameInput("Jane Doe"),
    cvv: showFieldErrors ? "1" : "123",
  });
  const [errors, setErrors] = useState<CardFormErrors>({
    ...(showFieldErrors
      ? {
          cardNumber: t("card.invalidNumber"),
          expiry: t("card.expiredCard"),
          cardHolderName: t("card.nameInvalid"),
          cvv: t("card.invalidCvv"),
        }
      : {}),
    ...(showBrandError
      ? { cardNumber: t("card.brandNotAccepted") }
      : {}),
  });

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const fieldColors = {
    background: colorsConfig.background,
    title: colorsConfig.title,
    bodyCopy,
    inactiveColor: colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const detectedBrand = useMemo(
    () => detectCardBrand(form.cardNumber),
    [form.cardNumber]
  );
  const detectedBrandInfo =
    detectedBrand && allowedBrands.includes(detectedBrand)
      ? CARD_BRANDS[detectedBrand]
      : null;

  const displayedBrands = BRAND_DISPLAY_ORDER.filter((key) =>
    allowedBrands.includes(key)
  );

  const brandsLabel = displayedBrands
    .map((key) => CARD_BRANDS[key]?.label)
    .filter(Boolean)
    .join(t("card.brandConjunction"));

  const handleChange = (field: keyof CardStepFields, value: string) => {
    const next =
      field === "cardHolderName" ? formatCardholderNameInput(value) : value;
    setForm((prev) => ({ ...prev, [field]: next }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): CardFormErrors => {
    const errs: CardFormErrors = {};
    const cleaned = form.cardNumber.replace(/\s/g, "");
    if (!passesLuhn(cleaned)) {
      errs.cardNumber = t("card.invalidNumber");
    } else {
      const brand = detectCardBrand(form.cardNumber);
      if (!brand || !allowedBrands.includes(brand)) {
        errs.cardNumber = t("card.brandNotAccepted");
      }
    }

    const [mm, yy] = form.expiry.split("/");
    if (!mm || !yy || !/^(0[1-9]|1[0-2])$/.test(mm) || !/^\d{2}$/.test(yy)) {
      errs.expiry = t("card.invalidExpiry");
    } else {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear() % 100;
      const expMonth = parseInt(mm, 10);
      const expYear = parseInt(yy, 10);
      if (
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth)
      ) {
        errs.expiry = t("card.expiredCard");
      }
    }

    if (!form.cardHolderName.trim()) {
      errs.cardHolderName = t("card.nameRequired");
    } else if (!isValidCardholderName(form.cardHolderName)) {
      errs.cardHolderName = t("card.nameInvalid");
    }
    if (!/^\d{3,4}$/.test(form.cvv)) errs.cvv = t("card.invalidCvv");

    return errs;
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    navigate(FUND_REQUEST_ROUTES.ADD_CARD_ADDRESS, { state: { card: form } });
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <AuraLayout colorConfig={colorsConfig}>
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("card.add")}
          onBackClick={() => navigate(-1)}
        />
      </Layout.Header>

      <Layout.Content className="collect">
        <ScrollFadeIndicator color={bodyCopy} />
        <form
          id="add-card-form"
          className="mock-card-form"
          onSubmit={handleSubmit}
        >
          <section className="mock-card-fields">
            <div className="mock-card-field">
              <FloatingField
                label={t("card.owner")}
                value={form.cardHolderName}
                required
                isValid={!errors.cardHolderName}
                colors={fieldColors}
              >
                <input
                  id="card-holder-name"
                  style={{ color: bodyCopy }}
                  value={form.cardHolderName}
                  onChange={(e) => handleChange("cardHolderName", e.target.value)}
                  maxLength={CARDHOLDER_NAME_INPUT_MAX_LENGTH}
                  autoComplete="cc-name"
                  autoCapitalize="characters"
                  spellCheck={false}
                  autoFocus
                />
              </FloatingField>
              {errors.cardHolderName && (
                <div style={errorStyle}>{errors.cardHolderName}</div>
              )}
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("card.number")}
                placeholder={t("card.numberPlaceholder")}
                value={form.cardNumber}
                required
                isValid={!errors.cardNumber}
                colors={fieldColors}
                adornment={
                  detectedBrandInfo ? (
                    <img
                      className="mock-card-brand"
                      src={detectedBrandInfo.logo}
                      alt={t(detectedBrandInfo.altKey)}
                    />
                  ) : undefined
                }
              >
                <input
                  id="card-number"
                  style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  value={form.cardNumber}
                  onChange={(e) =>
                    handleChange("cardNumber", formatCardNumber(e.target.value))
                  }
                  inputMode="numeric"
                  maxLength={23}
                />
              </FloatingField>
              {errors.cardNumber && (
                <div style={errorStyle}>{errors.cardNumber}</div>
              )}
            </div>

            <section className="vgs-inline">
              <div className="mock-card-field" style={{ flex: 1 }}>
                <FloatingField
                  label={t("card.expiryLabel")}
                  placeholder={t("card.expiryPlaceholder")}
                  value={form.expiry}
                  required
                  isValid={!errors.expiry}
                  colors={fieldColors}
                >
                  <input
                    id="card-expiry"
                    style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                    value={form.expiry}
                    onChange={(e) =>
                      handleChange("expiry", formatExpiry(e.target.value))
                    }
                    inputMode="numeric"
                  />
                </FloatingField>
                {errors.expiry && <div style={errorStyle}>{errors.expiry}</div>}
              </div>
              <div className="mock-card-field" style={{ flex: 1 }}>
                <FloatingField
                  label={t("card.cvvLabel")}
                  placeholder={t("card.cvvFormatPlaceholder")}
                  value={form.cvv}
                  required
                  isValid={!errors.cvv}
                  colors={fieldColors}
                >
                  <input
                    id="card-cvv"
                    style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                    type="password"
                    value={form.cvv}
                    onChange={(e) =>
                      handleChange("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    maxLength={4}
                  />
                </FloatingField>
                {errors.cvv && <div style={errorStyle}>{errors.cvv}</div>}
              </div>
            </section>
          </section>
        </form>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards">
          {!keyboardOpen && (
          <section className="valid-cards-container">
            <section className="valid-cards-logos">
              {displayedBrands.map((key) => {
                const brand = CARD_BRANDS[key];
                if (!brand) return null;
                return <img key={key} src={brand.logo} alt={t(brand.altKey)} />;
              })}
            </section>
            <BodyCopy className="footer-text" textColor={bodyCopy}>
              {t("card.acceptedBrands", { brands: brandsLabel })}
            </BodyCopy>
          </section>
          )}
          <section className="footer-section">
            <Button
              type="submit"
              form="add-card-form"
              size="large"
              color={textColorBtn}
              background={backgroundBtn}
            >
              {t("card.continue")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
